// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import BrandAlphabetNavigation from "@electrowire/Overrides/Widgets/Brand/BrandAlphabetNavigation";
import Notify from "@gcg/App/Components/Notify";
import { screen } from "@gcg/XDL/Helpers";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandAlphabetLetterModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, Radio, RadioGroup } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { RadioProps, RadioStyle } from "@insite/mobius/Radio";
import { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css, keyframes } from "styled-components";

const enum fields {
    // XNG-Changes
    showMarketSegmentFilters = "showMarketSegmentFilters",
    showFeaturedManufacturers = "showFeaturedManufacturers",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showMarketSegmentFilters]: boolean;
        [fields.showFeaturedManufacturers]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    allBrandsDataView: getAllBrandsDataView(state),
    brandAlphabetState: state.pages.brands.brandAlphabetState,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

type BrandLetterMap = {
    [letter: string]: BrandModel[];
};

const arrowUp = keyframes`
    from {
      transform: rotate(180deg) translateY(0);
    }
  
    to {
      transform: rotate(180deg) translateY(3px);
    }
`;

export interface BrandListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    brandLink?: LinkPresentationProps;
    buttonsWithShowScrollTopBtn?: InjectableCss;
    buttonsWithoutShowScrollTopBtn?: InjectableCss;
    buttonsWithShowScrollTopBtnUnderFooter?: InjectableCss;
    backToTopButton?: LinkPresentationProps;
    allBrandsTitle?: any;
    brandsList?: any;
    brandsItems?: GridContainerProps;
    brandItemName?: GridItemProps;
    brandLetter?: any;
    brandsSet?: any;
    backToTopIcon?: any;
    buttons?: InjectableCss;
    brandAlphabetSections?: InjectableCss;
    filterRadioGroup?: RadioGroupProps;
    filterRadio?: RadioProps;
    marketSegmentsWrapper?: InjectableCss;
    marketSegmentsTitle?: TypographyPresentationProps;
}

const sharedButtonsCss = css`
    display: flex;
    justify-content: flex-end;
    cursor: pointer;
    position: fixed;
    right: 15%;
    bottom: 100px;
    z-index: 9;
    transition: all 0.2s linear;

    &:hover {
        opacity: 1;
        visibility: visible;

        button {
            background: ${getColor("primary.main")};
            color: ${getColor("common.background")};
        }

        svg {
            animation: ${arrowUp} 0.4s ease infinite;
        }
    }
`;

export const listStyles: BrandListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    backToTopButton: {
        typographyProps: {
            size: 14,
            weight: 600,
            css: css`
                line-height: 20px;
                letter-spacing: 0;
                padding-bottom: 3px;
            `,
        },
    },
    backToTopIcon: {
        css: css`
            background: ${getColor("primary.main")};
            margin-right: 2px;
            border-radius: 3px 0 0 3px;
            width: 32px;
            justify-content: center;
            min-height: 40px;

            svg {
                transform: rotate(180deg);

                &,
                * {
                    fill: ${getColor("common.background")};
                }
            }
        `,
    },
    brandLink: {
        typographyProps: {
            size: 16,
            lineHeight: "26px",
            ellipsis: true,
        },
        css: css`
            &,
            span {
                color: ${getColor("text.main")} !important;
            }

            &,
            &:focus {
                outline: 0;
            }

            &:hover,
            &:focus {
                &,
                span {
                    color: ${getColor("primary.main")} !important;
                }
            }
        `,
    },
    buttonsWithShowScrollTopBtn: {
        css: css`
            visibility: visible;
            opacity: 0.75;
            ${sharedButtonsCss}
        `,
    },
    buttonsWithShowScrollTopBtnUnderFooter: {
        css: css`
            visibility: visible;
            opacity: 0.75;
            ${sharedButtonsCss}
            position : absolute;
            bottom: 150px;
            right: 5%;
            transition: none;
        `,
    },
    buttonsWithoutShowScrollTopBtn: {
        css: css`
            display: none !important;
            opacity: 0;
            ${sharedButtonsCss}
        `,
    },
    allBrandsTitle: {
        lineHeight: "32px",
        css: css`
            margin-bottom: 20px;
        `,
    },
    brandsList: {
        css: css`
            margin-bottom: 32px;
        `,
    },
    brandsItems: {
        gap: 0,
        css: css`
            ${GridOffset} {
                row-gap: 20px;
            }
        `,
    },
    brandItemName: {
        width: [6, 3, 3, 3, 3],
        css: css`
            padding-right: 25px;
        `,
    },
    brandLetter: {
        size: 32,
        lineHeight: "40px",
        weight: 600,
        transform: "uppercase",
        color: "common.ui6",
    },
    brandsSet: {
        css: css`
            padding: 32px 0;
            &:first-child {
                padding-top: 0;
            }
        `,
    },
    brandAlphabetSections: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            padding-bottom: 11px;
            margin-bottom: 20px;
            border-bottom: 1px solid ${getColor("common.ui3")};
        `,
    },
    filterRadioGroup: {
        css: css`
            flex-direction: row;
            column-gap: 32px;
            flex-wrap: wrap;
            
            & > ${RadioStyle} {
                display: inline-block;
            }

            ${screen("lt", "sm")`
                flex-direction: column;
                row-gap: 12px;
            `}
        `,
    },
    filterRadio: {
        css: css`
            &&&& {
                margin: 0 0 5px;
            }
        `,
    },
    marketSegmentsWrapper: {
        css: css`
            display: flex;
            padding: 20px 24px;
            background-color: ${getColor("common.ui2")};
            margin-bottom: 32px;
            align-items: center;
            column-gap: 32px;
            flex-wrap: wrap;
            ${screen("lt", "sm")`
                flex-direction: column;
                align-items: start;
                row-gap: 16px;
            `}
        `,
    },
    marketSegmentsTitle: {
        size: 20,
        weight: 600,
        css: css`
            &&&& {
                margin: 0;
            }
        `,
    }
};

const styles = listStyles;

interface XngBrandAlphabetLetterModel extends BrandAlphabetLetterModel {
    linkable: boolean
}

interface BrandListState {
    brandLetterDetails: XngBrandAlphabetLetterModel[];
    brandSections: BrandSection[];
}

/**
 * This will display all Brands grouped by the first character.
 * Starting with numbers grouped into '#'.
 * @param props
 */
const BrandList: FC<Props> = (props: Props) => {
    const { allBrandsDataView, brandAlphabetState, fields: { showFeaturedManufacturers, showMarketSegmentFilters } } = props;
    const [marketSegmentFilter, setMarketSegmentFilter] = React.useState("All Manufacturers");
    const brandList = allBrandsDataView.value || [];
    const brandAlphabet = (brandAlphabetState.value as any)?.alphabet || [];
    const marketSegmentFilters = (brandAlphabetState.value as any)?.properties?.marketSegments ? JSON.parse((brandAlphabetState.value as any).properties.marketSegments.replace(/'/g, '"')) : [];
    const [filterBrands, setFilterBrands] = useState("");
    const [selectBrandLetter, setSelectBrandLetter] = useState("-9");
    const [showScrollTopBtn, setShowScrollTopBtn] = useState(false);
    const { letters, brandLettersMap } = buildLettersAndMap(brandList);
    const [underFooter, setUnderFooter] = useState(false);
    const [mobileView, setMobieView] = useState(false);
    const { brandLetterDetails, brandSections } = buildBrandSections(
        letters,
        brandLettersMap,
        brandAlphabet,
        filterBrands,
    );
    const [state, setState] = React.useState<BrandListState>({ brandLetterDetails, brandSections });
    const [lastLetterClicked, setlastLetterClicked] = React.useState("");

    const marketSegmentFilterChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMarketSegmentFilter(event.currentTarget.value);

        const brandSections = buildBrandSections(letters, brandLettersMap, brandAlphabet, filterBrands, event.currentTarget.value);
        setState({
            ...state,
            brandLetterDetails: [...brandSections.brandLetterDetails],
            brandSections: brandSections.brandSections,
        });
    };

    React.useEffect(() => {
        setState({ ...state, brandLetterDetails, brandSections });

        window.addEventListener("scroll", e => {
            setShowScrollTopBtn(window.scrollY > 150);
            window.scrollY <= 150 && setShowScrollTopBtn(false);
            const elHeight = document.querySelectorAll("[data-test-selector='page_BrandsPage']");
            if (window.innerHeight + window.scrollY >= elHeight[0]?.clientHeight - 560 && window.innerWidth > 415) {
                setUnderFooter(true);
            }
            if (window.innerHeight + window.scrollY < elHeight[0]?.clientHeight - 560) {
                setUnderFooter(false);
            }
            if (window.innerWidth < 415) {
                setMobieView(true);
            }
            if (window.innerWidth > 415) {
                setMobieView(false);
            }
        });
    }, [brandLetterDetails?.length, brandSections?.length]);

    if (brandList.length === 0) {
        return null;
    }

    const handleBackToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleFilterBrands = (e: any) => {
        const target = e.target;
        const value = target.value;

        setFilterBrands(value);

        const brandSections = buildBrandSections(letters, brandLettersMap, brandAlphabet, value, marketSegmentFilter);

        setState({
            ...state,
            brandLetterDetails: brandSections.brandLetterDetails,
            brandSections: brandSections.brandSections,
        });
    };

    const handleSelectBrandLetterChange = (e: any) => {
        const target = e.target;
        const value = target.value;

        setSelectBrandLetter(value);

        const letterElement = document.getElementById(`letter-${value === "#" ? "number" : value}`);

        if (letterElement) {
            window.scrollTo(0, letterElement.offsetTop + 450);
        }
    };

    const brandsCount =
        state.brandSections &&
        state.brandSections.length > 0 &&
        state.brandSections.filter(child => {
            return child.brandMap.length > 0;
        }).length;

    const fixedButtomOptions = () => {
        let returnStyle: InjectableCss | undefined = styles.buttonsWithShowScrollTopBtn;
        if (showScrollTopBtn && !underFooter) {
            returnStyle = styles.buttonsWithShowScrollTopBtn;
        }
        if (!showScrollTopBtn) {
            returnStyle = styles.buttonsWithoutShowScrollTopBtn;
        }
        if (underFooter) {
            returnStyle = styles.buttonsWithShowScrollTopBtnUnderFooter;
        }

        return returnStyle;
    };

    return (
        <>
            {showMarketSegmentFilters &&
                <StyledWrapper {...styles.marketSegmentsWrapper}>
                    <Typography {...styles.marketSegmentsTitle}>
                        {translate("Filter Manufacturers")}
                    </Typography>
                    <RadioGroup
                        {...styles.filterRadioGroup}
                        value={marketSegmentFilter}
                        onChangeHandler={marketSegmentFilterChangeHandler}
                    >
                        <Radio {...styles.filterRadio} value="All Manufacturers">
                            {translate("All Manufacturers")}
                        </Radio>
                        {marketSegmentFilters?.map((msFilter: string) => (
                            <Radio {...styles.filterRadio} value={msFilter} key={msFilter}>
                                {msFilter}
                            </Radio>
                        ))}
                    </RadioGroup>
                </StyledWrapper>
            }
            {showFeaturedManufacturers && createWidgetElement("Brand/BrandGallery", {
                fields: {
                    title: "featured manufacturers",
                    boldTitle: true,
                    uppercaseTitle: true,
                    maximumNumberOfBrandsPerRow: "6",
                    numberOfBrandsToDisplay: "6",
                },
            })}
            <StyledWrapper
                id="brandlist"
                data-test-selector="brandList"
                css={css`
                position: relative;
            `}
            >
                {
                    state.brandLetterDetails && state.brandLetterDetails.length &&
                    <BrandAlphabetNavigation
                        letterDetails={state.brandLetterDetails}
                        filterBrands={filterBrands}
                        handleFilterBrands={handleFilterBrands}
                        selectBrandLetter={selectBrandLetter}
                        handleSelectBrandLetterChange={handleSelectBrandLetterChange}
                    />
                }

                {!!brandsCount && brandsCount > 0 && (
                    <StyledWrapper {...styles.brandsList}>
                        {state.brandSections.map(brandDetails => {
                            if (!brandDetails.brandMap.length) {
                                return;
                            }

                            return (
                                <StyledWrapper key={brandDetails.letter} {...styles.brandsSet}>
                                    <StyledWrapper {...styles.brandAlphabetSections}>
                                        <Typography
                                            as="div"
                                            {...styles.brandLetter}
                                            id={`letter-${brandDetails.letter === "#" ? "number" : brandDetails.letter}`}
                                        >
                                            {brandDetails.letter}
                                        </Typography>
                                        <Link {...styles.backToTopButton} onClick={handleBackToTop}>
                                            {translate("Back to Top")}
                                        </Link>
                                    </StyledWrapper>
                                    <GridContainer {...styles.brandsItems}>
                                        {brandDetails.brandMap.map(brand => (
                                            <GridItem key={brand.detailPagePath} {...styles.brandItemName}>
                                                <Link
                                                    {...styles.brandLink}
                                                    href={brand.detailPagePath}
                                                    data-test-selector="brandLink"
                                                >
                                                    {brand.name}
                                                </Link>
                                            </GridItem>
                                        ))}
                                    </GridContainer>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                )}
                {!brandsCount && (
                    <StyledWrapper>
                        <Notify messageType="danger" body={siteMessage("Brand_NotFound")} />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BrandList),
    definition: {
        group: "Brands",
        icon: "List",
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showMarketSegmentFilters,
                displayName: "Show Market Segment Filters",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showFeaturedManufacturers,
                displayName: "Show Featured Manufacturers",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;

interface BrandSection {
    letter: string;
    brandMap: BrandModel[];
}

/**
 * Takes in a brand list and sorts it into #, A-Z, Other characters.
 *
 * @param brandList The list of brands that should be sorted.
 */
const buildLettersAndMap = (brandList: BrandModel[]): { letters: string[]; brandLettersMap: BrandLetterMap } => {
    const brandLettersMap: BrandLetterMap = {};

    let letters: string[] = [];
    let letter: string;
    let newLetters: string[] = letters;

    for (let i = 0; i < brandList.length; i = i + 1) {
        const brand = brandList[i];

        letter = brand.name[0] ? brand.name[0].toLowerCase() : "";

        if (isNumeric(letter)) {
            letter = "0-9";
        }

        if (!brandLettersMap[letter]) {
            brandLettersMap[letter] = [];
        }

        brandLettersMap[letter].push(brand);
        newLetters = [...newLetters, letter];
    }

    letters = newLetters.filter((letter, index, array) => array.indexOf(letter) === index);

    if (brandLettersMap["#"]) {
        brandLettersMap["#"].sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    return { letters, brandLettersMap };
};

/**
 * Take in the props and create easier to work with state objects.
 *
 * @param letters List of letters and special characters.
 * @param brandLettersMap Brand list sorted into letter lists.
 * @param brandAlphabet Letter count list.
 * @param search The search value to use if not undefined.
 */
const buildBrandSections = (
    letters: string[],
    brandLettersMap: BrandLetterMap,
    brandAlphabet: BrandAlphabetLetterModel[],
    search?: string | undefined,
    filter?: string | undefined,
): {
    brandLetterDetails: {
        count: number;
        letter: string;
        linkable: boolean;
    }[];
    brandSections: {
        letter: string;
        brandMap: BrandModel[];
    }[];
} => {
    const brandLetterDetails = brandAlphabet.map(alphabetItem => ({
        ...alphabetItem,
        linkable: filter && filter !== "All Manufacturers" ? brandLettersMap[alphabetItem.letter]?.filter(brand => (brand.properties?.marketSegments || "").toLowerCase().includes(filter.toLowerCase()))?.length > 0: alphabetItem.count > 0,
    }));
    const brandSections = letters.map(letter => ({
        letter,
        brandMap: getBrandLettersMap(brandLettersMap, letter, search, filter),
    }));

    return { brandLetterDetails, brandSections };
};

const getBrandLettersMap = (brandLettersMap: BrandLetterMap, letter: string, search?: string | undefined, filter?: string | undefined) => {
    let result = brandLettersMap[letter];
    if (search) {
        result = result.filter(brand => (brand.name || "").toLowerCase().includes(search.toLowerCase()));
    }
    if (filter && filter !== "All Manufacturers") {
        result = result.filter(brand => (brand.properties?.marketSegments || "").toLowerCase().includes(filter.toLowerCase()));
    }

    return result;
};